import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { SkipFinanceService } from '@app/configure/services/skip-finance.service';
import { ConfigureService } from '@app/configure/services/configure.service';
import { ModalService } from '@app/core/services/modal.service';
import { HelperService } from '@app/core/services/helper.service';
import { ToastService } from '@app/core/services/toast.service';

import { ModalAction } from '@app/core/models/modal-action.model';

@Component({
	selector: 'cng-configure-customise-review',
	templateUrl: './customise-review.component.html',
	styleUrls: ['./customise-review.component.less'],
})
export class ConfigureCustomiseReviewComponent implements OnInit, OnDestroy {
	//  COMPONENT VARIABLES

	current_step_key: string = 'customise_review';
	step_mode: string = this.configure_service.getStepMode();
	vehicle: any = this.configure_service.configure_data.steps.purchase_vehicle.data;

	finance_available: boolean = this.configure_service.configure_data.meta.finance.available;
	finance_failed: boolean = this.configure_service.configure_data.meta.finance.failed;
	cashback_deal: boolean = this.configure_service.configure_data.meta.cashback_deal;
	show_addon_finance: boolean = this.finance_available && !this.finance_failed && !this.cashback_deal;
	finance_meta: any = this.configure_service.configure_data.meta.finance;
	stacked_quotes: any = this.configure_service.configure_data.steps.finance_options.data;
	selected_quote: any = this.helper_service.copy(this.stacked_quotes).find((quote) => {
		return quote.selected;
	});
	part_exchanges: any = this.configure_service.configure_data.steps.part_exchange.data.filter((item) => {
		return item.vehicle !== null;
	});
	pex_enabled: boolean = this.configure_service.isStepEnabled('part_exchange') || this.part_exchanges.length;
	vehicle_per_month: number = this.configure_service.configure_data.steps.customise_review.data.vehicle_per_month;
	addons: any[] = this.configure_service.configure_data.steps.customise_review.data.addons;
	addons_subtotal: any = {
		cash: 0,
		per_month: 0,
	};
	grand_total: number = this.configure_service.configure_data.steps.customise_review.data.grand_total;
	grand_total_net: number = this.configure_service.configure_data.steps.customise_review.data.grand_total_net;
	grand_total_vat: number = this.configure_service.configure_data.steps.customise_review.data.grand_total_vat;
	pex_allowance_pm: number = this.configure_service.configure_data.steps.customise_review.data.pex_allowance_pm;
	pex_settlement_pm: number = this.configure_service.configure_data.steps.customise_review.data.pex_settlement_pm;

	lte_xs: boolean = false;
	loading: boolean = false;
	destroyed$: Subject<boolean> = new Subject<boolean>();
	has_videos: boolean = false;

	constructor(
		private configure_service: ConfigureService,
		private helper_service: HelperService,
		private toast_service: ToastService,
		private modal_service: ModalService,
		private skip_finance_service: SkipFinanceService
	) {
		if (!this.configure_service.isStepEnabled('customise_review')) {
			this.configure_service.navigateToStep('purchase_vehicle');
		}
	}

	ngOnInit(): void {
		this.checkScreenWidth();
		this.setAddonAdditional();

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.vehicle = configure_data.steps.purchase_vehicle.data;
			this.finance_available = configure_data.meta.finance.available;
			this.finance_failed = configure_data.meta.finance.failed;
			this.cashback_deal = configure_data.meta.cashback_deal;
			this.show_addon_finance = this.finance_available && !this.finance_failed && !this.cashback_deal;
			this.finance_meta = configure_data.meta.finance;
			this.stacked_quotes = configure_data.steps.finance_options.data;
			this.selected_quote = this.helper_service.copy(this.stacked_quotes).find((quote) => {
				return quote.selected;
			});
			this.part_exchanges = configure_data.steps.part_exchange.data.filter((item) => {
				return item.vehicle !== null;
			});
			this.vehicle_per_month = configure_data.steps.customise_review.data.vehicle_per_month;
			this.addons = configure_data.steps.customise_review.data.addons;
			this.grand_total = configure_data.steps.customise_review.data.grand_total;
			this.grand_total_net = configure_data.steps.customise_review.data.grand_total_net;
			this.grand_total_vat = configure_data.steps.customise_review.data.grand_total_vat;
			this.pex_allowance_pm = configure_data.steps.customise_review.data.pex_allowance_pm;
			this.pex_settlement_pm = configure_data.steps.customise_review.data.pex_settlement_pm;
			this.setAddonAdditional();
		});

		this.configure_service.track('step.moved', { step: this.current_step_key }).subscribe();
		this.configure_service.trackJourney().subscribe();

		/**
		 * Check if each addon have video platform
		 */
		this.addons.some((addon: any) => {
			if (!!(addon.video_id && addon.video_platform)) {
				this.has_videos = true;
			}
		});
	}

	setAddonAdditional(): void {
		this.addons_subtotal = {
			cash: 0,
			per_month: 0,
		};

		for (const a of this.addons) {
			//  CREATE SIBLING OPTIONS OBJECT ON EACH ADDON TO CONTAIN ALL OF THE SELECTED OPTIONS

			a.selected_options = a.options.filter((o) => {
				return o.selected;
			});

			//  TALLY UP TOTALS FOR SELECTED OPTIONS

			a.total_cash = 0;
			a.total_per_month = 0;

			a.total_cash = a.selected_options.reduce((total, option) => {
				if (option.vat && this.vehicle.add_vat) {
					a.has_vq_option = true;
				}

				return total + (option.vat && this.vehicle.add_vat ? option.net_amount : option.amount);
			}, 0);

			a.total_per_month = a.selected_options.reduce((total, option) => {
				return total + option.per_month;
			}, 0);

			this.addons_subtotal.cash += a.total_cash;
			this.addons_subtotal.per_month += a.total_per_month;
		}
	}

	setEditAddon(addon, addon_index): void {
		this.modal_service.show(['configure/addon'], { addon_index: addon_index });
	}

	@HostListener('window:resize', ['$event'])
	checkScreenWidth(): void {
		this.lte_xs = this.helper_service.isScreenWidth('<=', 'xs');
	}

	jumpTo(new_step_key: string): void {
		// this.skip_finance_service.onJumpSteps(new_step_key, (canProceed: boolean) => {
		// 	if (canProceed) {
		this.saveAndGoTo(new_step_key);
		// 	}
		// });
	}

	saveAndGoTo(new_step_key: string): void {
		this.configure_service.navigateToStep(new_step_key);
	}

	buildBrandLogoUrl(make: string): string {
		return this.helper_service.buildManufacturerBrandLogoUrl(make);
	}

	buildAddonLogoUrl(filename: string): string {
		return this.helper_service.buildAddonBrandLogoUrl(filename);
	}

	onAddPartExchange(): void {
		if (this.finance_meta.locked) {
			this.modal_service.show(['configure/finance-locked']);
			return;
		}

		if (
			this.configure_service.configure_data.steps.part_exchange.data === null ||
			this.configure_service.configure_data.steps.part_exchange.data.length < 1
		) {
			this.configure_service.configure_data.steps.part_exchange.data = [
				{ state: 'a', registration: null, cap_codes: null, vehicle: null },
			];
			this.configure_service.setData();
		}

		this.jumpTo('part_exchange');
	}

	onEditPartExchange(): void {
		if (this.finance_meta.locked) {
			this.modal_service.show(['configure/finance-locked']);
			return;
		}

		this.configure_service.configure_data.steps.part_exchange.data[0].force_state = 'b';
		this.configure_service.setData();
		this.jumpTo('part_exchange');
	}

	onRemovePex(): void {
		if (this.finance_meta.locked) {
			this.modal_service.show(['configure/finance-locked']);
			return;
		}

		this.modal_service.showConfirm({
			name: 'part_exchange_remove_modal',
			title: 'Remove part exchange vehicle?',
			content: ['Are you sure you want to remove this part exchange vehicle?'],
			actions: [
				new ModalAction({
					id: 'remove',
					name: 'Remove',
					bias: 'negative',
					grow: false,
					showLoading: true,
					click: (action: ModalAction) => {
						this.removePartExchange(action);
					},
				}),
			],
		});
	}

	removePartExchange(action: ModalAction): void {
		this.configure_service.deletePartExVehicle(false).subscribe((response1) => {
			this.configure_service.saveCustomiseReview(true).subscribe((response2) => {
				if (response1 && response2) {
					this.toast_service.success('Part Exchange Vehicle Removed');
					action.loading = false;
					this.modal_service.close();
				}
			});
		});
	}

	getRemainingAddons(addon: any): number {
		return addon.selected_options.length ? addon.options.length - addon.selected_options.length : 0;
	}

	hasFollowingSteps(): boolean {
		return (
			this.configure_service.isStepEnabled('finance_options') ||
			this.configure_service.isStepEnabled('delivery_options') ||
			this.configure_service.isStepEnabled('payment')
		);
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
